import { v4 as uuidv4 } from 'uuid';
// Note: Temporal polyfill should be imported by the consuming application
// import '@js-temporal/polyfill/global';

import { ValidationError, BookingConflictError, MemberPermissionError } from '../utils/errors.js';
import { validateBooking, validateMember } from '../utils/validators.js';
import { isTimeSlotAvailable, calculateDuration } from '../utils/calculators.js';

import { Green } from './Green.js';
import { Rink } from './Rink.js';
import { Booking } from './Booking.js';
import { BookingType } from './BookingType.js';
import { AuditLogEntry } from './AuditLogEntry.js';
import { TimeSlot } from './TimeSlot.js';

/**
 * Default system settings configuration
 */
const DEFAULT_SETTINGS = {
  bookingRules: {
    maxAdvanceBookingDays: 30,
    minAdvanceBookingHours: 2,
    maxBookingDuration: 180, // minutes
    minBookingDuration: 60,  // minutes
    allowConcurrentBookings: false,
    requireApproval: false
  },
  dataRetention: {
    bookingRetentionDays: 365,     // Keep bookings for 1 year
    auditLogRetentionDays: 1095,   // Keep audit logs for 3 years
    autoCleanupEnabled: true,      // Enable automatic cleanup
    cleanupIntervalDays: 30        // Run cleanup every 30 days
  },
  notifications: {
    sendConfirmationEmail: true,
    sendReminderEmail: true,
    reminderHours: 24
  }
};

/**
 * The primary class managing all booking operations
 */
export class BookingSystem {
  /**
   * Create a new BookingSystem
   * @param {Object} data - BookingSystem configuration
   * @param {string} [data._id] - Unique identifier (auto-generated if not provided)
   * @param {string} data.club - ID of the club that owns this booking system
   * @param {Object} [data.settings] - Configuration object for booking rules
   * @param {Array} [data.greens] - Array of Green instances or data
   * @param {Array} [data.bookings] - Array of Booking instances or data
   * @param {Array} [data.bookingTypes] - Array of BookingType instances or data
   * @param {Array} [data.members] - Array of member objects with {_id: GUID, name: string, preferences: Object}
   * @param {Array} [data.timeSlots] - Array of TimeSlot instances or data
   * @param {Array} [data.auditLog] - Array of audit log entries
   */
  constructor(data) {
    if (!data || typeof data !== 'object') {
      throw new ValidationError('BookingSystem data is required');
    }

    if (!data.club || typeof data.club !== 'string') {
      throw new ValidationError('Club ID is required', 'club', data.club);
    }

    this._id = data._id || uuidv4();
    this.club = data.club;
    this.settings = { ...DEFAULT_SETTINGS, ...(data.settings || {}) };
    
    // Initialize collections
    this.greens = [];
    this.bookings = [];
    this.bookingTypes = [];
    this.timeSlots = [];
    this.auditLog = [];
    this.members = [];

    // Initialize members from data
    if (Array.isArray(data.members)) {
      this.members = data.members.map(member => ({
        _id: member._id,
        name: member.name,
        preferences: { ...member.preferences }
      }));
    }

    // Initialize greens
    if (Array.isArray(data.greens)) {
      for (const greenData of data.greens) {
        this.addGreen(greenData instanceof Green ? greenData : Green.fromJSON(JSON.stringify(greenData)));
      }
    }

    // Initialize booking types (add defaults if none provided)
    if (Array.isArray(data.bookingTypes) && data.bookingTypes.length > 0) {
      for (const bookingTypeData of data.bookingTypes) {
        this.addBookingType(bookingTypeData instanceof BookingType ? bookingTypeData : BookingType.fromJSON(JSON.stringify(bookingTypeData)));
      }
    } else {
      // Add default booking types
      for (const defaultType of BookingType.getDefaults()) {
        this.addBookingType(defaultType);
      }
    }

    // Initialize bookings
    if (Array.isArray(data.bookings)) {
      for (const bookingData of data.bookings) {
        this.bookings.push(bookingData instanceof Booking ? bookingData : Booking.fromJSON(JSON.stringify(bookingData)));
      }
    }

    // Initialize time slots
    if (Array.isArray(data.timeSlots)) {
      for (const timeSlotData of data.timeSlots) {
        this.timeSlots.push(timeSlotData instanceof TimeSlot ? timeSlotData : TimeSlot.fromJSON(JSON.stringify(timeSlotData)));
      }
    }

    // Initialize audit log
    if (Array.isArray(data.auditLog)) {
      for (const auditData of data.auditLog) {
        this.auditLog.push(auditData instanceof AuditLogEntry ? auditData : AuditLogEntry.fromJSON(JSON.stringify(auditData)));
      }
    }

    this._lastCleanup = data._lastCleanup ? new Date(data._lastCleanup) : new Date();
  }

  /**
   * Add a green to the system
   * @param {Green|Object} green - Green instance or green data
   * @returns {BookingSystem} This instance for chaining
   */
  addGreen(green) {
    let greenInstance;
    if (green instanceof Green) {
      greenInstance = green;
    } else if (green && typeof green === 'object') {
      greenInstance = new Green(green);
    } else {
      throw new ValidationError('Valid green data or Green instance is required', 'green', green);
    }

    // Check for duplicate green names
    const existingGreen = this.greens.find(g => g.name === greenInstance.name);
    if (existingGreen) {
      throw new ValidationError(`Green with name '${greenInstance.name}' already exists`, 'greenName', greenInstance.name);
    }

    this.greens.push(greenInstance);
    return this;
  }

  /**
   * Remove a green from the system
   * @param {string} greenId - Green ID to remove
   * @param {string} memberId - Member performing the action
   * @returns {BookingSystem} This instance for chaining
   */
  removeGreen(greenId, memberId) {
    validateMember(memberId, 'remove_green');

    const index = this.greens.findIndex(green => green._id === greenId);
    if (index === -1) {
      throw new ValidationError('Green not found', 'greenId', greenId);
    }

    const green = this.greens[index];

    // Check for existing bookings on this green
    const greenBookings = this.getRinkBookings(greenId);
    const futureBookings = greenBookings.filter(booking => 
      booking.isFuture() && (booking.status === 'pending' || booking.status === 'confirmed')
    );

    if (futureBookings.length > 0) {
      throw new ValidationError(
        'Cannot remove green with active future bookings',
        'activeBookings',
        futureBookings.length
      );
    }

    // Add audit log entry
    this._addAuditEntry(AuditLogEntry.deleteEntry('green', greenId, memberId, JSON.parse(green.toJSON())));

    this.greens.splice(index, 1);
    return this;
  }

  /**
   * Add a booking type to the system
   * @param {BookingType|Object} bookingType - BookingType instance or data
   * @returns {BookingSystem} This instance for chaining
   */
  addBookingType(bookingType) {
    let bookingTypeInstance;
    if (bookingType instanceof BookingType) {
      bookingTypeInstance = bookingType;
    } else if (bookingType && typeof bookingType === 'object') {
      bookingTypeInstance = new BookingType(bookingType);
    } else {
      throw new ValidationError('Valid booking type data or BookingType instance is required', 'bookingType', bookingType);
    }

    // Check for duplicate booking type names
    const existingType = this.bookingTypes.find(t => t.name === bookingTypeInstance.name);
    if (existingType) {
      throw new ValidationError(`Booking type with name '${bookingTypeInstance.name}' already exists`, 'bookingTypeName', bookingTypeInstance.name);
    }

    this.bookingTypes.push(bookingTypeInstance);
    return this;
  }

  /**
   * Remove a booking type from the system
   * @param {string} bookingTypeId - Booking type ID to remove
   * @param {string} memberId - Member performing the action
   * @returns {BookingSystem} This instance for chaining
   */
  removeBookingType(bookingTypeId, memberId) {
    validateMember(memberId, 'remove_booking_type');

    const index = this.bookingTypes.findIndex(type => type._id === bookingTypeId);
    if (index === -1) {
      throw new ValidationError('Booking type not found', 'bookingTypeId', bookingTypeId);
    }

    // Check for existing bookings using this type
    const typeBookings = this.bookings.filter(booking => booking.bookingTypeId === bookingTypeId);
    const futureBookings = typeBookings.filter(booking => 
      booking.isFuture() && (booking.status === 'pending' || booking.status === 'confirmed')
    );

    if (futureBookings.length > 0) {
      throw new ValidationError(
        'Cannot remove booking type with active future bookings',
        'activeBookings',
        futureBookings.length
      );
    }

    const bookingType = this.bookingTypes[index];
    
    // Add audit log entry
    this._addAuditEntry(AuditLogEntry.deleteEntry('bookingType', bookingTypeId, memberId, JSON.parse(bookingType.toJSON())));

    this.bookingTypes.splice(index, 1);
    return this;
  }

  /**
   * Create a new booking
   * @param {Object} bookingData - Booking data
   * @param {string} memberId - Member creating the booking
   * @returns {Booking} Created booking instance
   */
  createBooking(bookingData, memberId) {
    validateMember(memberId, 'create_booking');
    validateBooking(bookingData);

    // Validate booking rules
    this._validateBookingRules(bookingData);

    // Find the green and rink
    const green = this._findGreenByRinkId(bookingData.rinkId);
    if (!green) {
      throw new ValidationError('Rink not found in any green', 'rinkId', bookingData.rinkId);
    }

    // Validate the booking can be made
    green.validateBooking(
      bookingData.rinkId,
      bookingData.bookingTypeId,
      bookingData.date,
      bookingData.start,
      bookingData.end,
      this.bookings
    );

    // Check for conflicts
    if (!this.settings.bookingRules.allowConcurrentBookings) {
      const conflicts = this.findBookingConflicts(
        bookingData.rinkId,
        bookingData.date,
        bookingData.start,
        bookingData.end
      );

      if (conflicts.length > 0) {
        throw new BookingConflictError('Booking conflicts with existing reservation', conflicts[0]);
      }
    }

    // Create the booking
    const booking = new Booking({
      ...bookingData,
      memberId: bookingData.memberId || memberId,
      status: this.settings.bookingRules.requireApproval ? 'pending' : 'confirmed'
    });

    this.bookings.push(booking);

    // Add audit log entry
    this._addAuditEntry(AuditLogEntry.createEntry('booking', booking._id, memberId, JSON.parse(booking.toJSON())));

    return booking;
  }

  /**
   * Modify an existing booking
   * @param {string} bookingId - Booking ID to modify
   * @param {Object} updates - Updates to apply
   * @param {string} memberId - Member making the modification
   * @returns {Booking} Updated booking instance
   */
  modifyBooking(bookingId, updates, memberId) {
    validateMember(memberId, 'modify_booking');

    const booking = this.bookings.find(b => b._id === bookingId);
    if (!booking) {
      throw new ValidationError('Booking not found', 'bookingId', bookingId);
    }

    if (!booking.canModify()) {
      throw new ValidationError(`Cannot modify booking with status: ${booking.status}`, 'status', booking.status);
    }

    // Store old values for audit log
    const oldValues = JSON.parse(booking.toJSON());

    // If time/date changes, validate rules and conflicts
    if (updates.date || updates.start || updates.end || updates.rinkId) {
      const newDate = updates.date ? new Date(updates.date) : booking.date;
      const newStart = updates.start ? new Date(updates.start) : booking.start;
      const newEnd = updates.end ? new Date(updates.end) : booking.end;
      const newRinkId = updates.rinkId || booking.rinkId;

      // Validate booking rules for new time
      this._validateBookingRules({
        date: newDate,
        start: newStart,
        end: newEnd,
        rinkId: newRinkId,
        bookingTypeId: updates.bookingTypeId || booking.bookingTypeId
      });

      // Check for conflicts (excluding this booking)
      if (!this.settings.bookingRules.allowConcurrentBookings) {
        const conflicts = this.findBookingConflicts(newRinkId, newDate, newStart, newEnd, bookingId);
        if (conflicts.length > 0) {
          throw new BookingConflictError('Modified booking conflicts with existing reservation', conflicts[0]);
        }
      }

      // If rink changed, validate the new green
      if (updates.rinkId && updates.rinkId !== booking.rinkId) {
        const green = this._findGreenByRinkId(updates.rinkId);
        if (!green) {
          throw new ValidationError('New rink not found in any green', 'rinkId', updates.rinkId);
        }

        green.validateBooking(
          updates.rinkId,
          updates.bookingTypeId || booking.bookingTypeId,
          newDate,
          newStart,
          newEnd,
          this.bookings,
          bookingId
        );
      }
    }

    // Apply updates
    booking.update(updates, memberId);

    // Add audit log entry
    this._addAuditEntry(AuditLogEntry.updateEntry('booking', bookingId, memberId, oldValues, JSON.parse(booking.toJSON())));

    return booking;
  }

  /**
   * Cancel an existing booking
   * @param {string} bookingId - Booking ID to cancel
   * @param {string} memberId - Member canceling the booking
   * @returns {Booking} Cancelled booking instance
   */
  cancelBooking(bookingId, memberId) {
    validateMember(memberId, 'cancel_booking');

    const booking = this.bookings.find(b => b._id === bookingId);
    if (!booking) {
      throw new ValidationError('Booking not found', 'bookingId', bookingId);
    }

    if (!booking.canCancel()) {
      throw new ValidationError('Booking cannot be cancelled', 'status', booking.status);
    }

    const oldStatus = booking.status;
    booking.updateStatus('cancelled', memberId);

    // Add audit log entry
    this._addAuditEntry(AuditLogEntry.updateEntry(
      'booking', 
      bookingId, 
      memberId, 
      { status: oldStatus }, 
      { status: 'cancelled' }
    ));

    return booking;
  }

  /**
   * Get available time slots for a rink
   * @param {string} rinkId - Rink ID
   * @param {Date} date - Date to check
   * @param {number} duration - Duration in minutes
   * @returns {Array} Available time slots
   */
  getAvailability(rinkId, date, duration = 60) {
    if (!rinkId || !date) {
      return [];
    }

    const green = this._findGreenByRinkId(rinkId);
    if (!green) {
      return [];
    }

    const rink = green.getRink(rinkId);
    if (!rink) {
      return [];
    }

    // Get bookings for this rink and date
    const dayBookings = this.bookings.filter(booking =>
      booking.rinkId === rinkId &&
      booking.date.toDateString() === date.toDateString() &&
      booking.status !== 'cancelled'
    );

    // Generate potential time slots (e.g., hourly from 8 AM to 6 PM)
    const availableSlots = [];
    const startHour = 8;
    const endHour = 18;

    for (let hour = startHour; hour < endHour; hour++) {
      const slotStart = new Date(date);
      slotStart.setHours(hour, 0, 0, 0);
      
      const slotEnd = new Date(slotStart);
      slotEnd.setMinutes(slotEnd.getMinutes() + duration);

      // Check if this slot is available
      if (isTimeSlotAvailable(slotStart, slotEnd, dayBookings)) {
        availableSlots.push({
          start: slotStart,
          end: slotEnd,
          duration: duration
        });
      }
    }

    return availableSlots;
  }

  /**
   * Get bookings for a specific rink within a date range
   * @param {string} rinkId - Rink ID
   * @param {Object} dateRange - Date range with start and end
   * @returns {Array} Filtered bookings
   */
  getRinkBookings(rinkId, dateRange = null) {
    let rinkBookings = this.bookings.filter(booking => booking.rinkId === rinkId);

    if (dateRange && dateRange.start && dateRange.end) {
      rinkBookings = rinkBookings.filter(booking => {
        const bookingDate = new Date(booking.date);
        return bookingDate >= dateRange.start && bookingDate <= dateRange.end;
      });
    }

    return rinkBookings.sort((a, b) => a.start - b.start);
  }

  /**
   * Get all bookings for a member (using GUID)
   * @param {string} memberId - Member GUID
   * @param {Object} dateRange - Optional date range
   * @returns {Array} Member's bookings
   */
  getMemberBookings(memberId, dateRange = null) {
    validateMember(memberId);

    let memberBookings = this.bookings.filter(booking => booking.memberId === memberId);

    if (dateRange && dateRange.start && dateRange.end) {
      memberBookings = memberBookings.filter(booking => {
        const bookingDate = new Date(booking.date);
        return bookingDate >= dateRange.start && bookingDate <= dateRange.end;
      });
    }

    return memberBookings.sort((a, b) => a.start - b.start);
  }

  /**
   * Get member's booking history within a date range
   * @param {string} memberId - Member GUID
   * @param {Object} dateRange - Date range with start and end
   * @returns {Array} Member's booking history
   */
  getMemberBookingHistory(memberId, dateRange) {
    validateMember(memberId);

    if (!dateRange || !dateRange.start || !dateRange.end) {
      throw new ValidationError('Date range with start and end is required');
    }

    return this.getMemberBookings(memberId, dateRange);
  }

  /**
   * Get member's booking preferences
   * @param {string} memberId - Member GUID
   * @returns {Object|null} Member's preferences or null
   */
  getMemberPreferences(memberId) {
    validateMember(memberId);
    const member = this.members.find(m => m._id === memberId);
    return member ? member.preferences : null;
  }

  /**
   * Set member's booking preferences
   * @param {string} memberId - Member GUID
   * @param {Object} preferences - Preferences to set
   * @returns {BookingSystem} This instance for chaining
   */
  setMemberPreferences(memberId, preferences) {
    validateMember(memberId);

    if (!preferences || typeof preferences !== 'object') {
      throw new ValidationError('Valid preferences object is required', 'preferences', preferences);
    }

    const member = this.members.find(m => m._id === memberId);
    if (member) {
      member.preferences = { ...preferences };
    } else {
      // If member doesn't exist, create a new member with minimal data
      this.members.push({
        _id: memberId,
        name: '', // Empty name as it should be provided by host environment
        preferences: { ...preferences }
      });
    }
    return this;
  }

  /**
   * Get system-wide booking statistics
   * @param {Object} dateRange - Optional date range
   * @returns {Object} System statistics
   */
  getSystemStats(dateRange = null) {
    let bookingsToAnalyze = this.bookings;

    if (dateRange && dateRange.start && dateRange.end) {
      bookingsToAnalyze = bookingsToAnalyze.filter(booking => {
        const bookingDate = new Date(booking.date);
        return bookingDate >= dateRange.start && bookingDate <= dateRange.end;
      });
    }

    const confirmedBookings = bookingsToAnalyze.filter(b => b.status === 'confirmed' || b.status === 'completed');
    const totalMinutes = confirmedBookings.reduce((sum, booking) => sum + booking.getDuration(), 0);

    const greenStats = this.greens.map(green => {
      if (dateRange) {
        return green.getUtilizationStats(this.bookings, dateRange.start, dateRange.end);
      }
      // For no date range, use last 30 days
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      return green.getUtilizationStats(this.bookings, start, end);
    });

    return {
      totalGreens: this.greens.length,
      totalRinks: this.greens.reduce((sum, green) => sum + green.rinks.length, 0),
      totalBookings: bookingsToAnalyze.length,
      confirmedBookings: confirmedBookings.length,
      totalBookedMinutes: totalMinutes,
      averageBookingDuration: confirmedBookings.length > 0 ? totalMinutes / confirmedBookings.length : 0,
      greenStats,
      bookingsByStatus: {
        pending: bookingsToAnalyze.filter(b => b.status === 'pending').length,
        confirmed: bookingsToAnalyze.filter(b => b.status === 'confirmed').length,
        cancelled: bookingsToAnalyze.filter(b => b.status === 'cancelled').length,
        completed: bookingsToAnalyze.filter(b => b.status === 'completed').length
      }
    };
  }

  /**
   * Remove bookings older than cutoff date
   * @param {Date} cutoffDate - Date before which bookings should be removed
   * @param {string} memberId - Member performing the cleanup
   * @returns {number} Number of bookings removed
   */
  clearOldBookings(cutoffDate, memberId) {
    validateMember(memberId, 'cleanup_bookings');

    if (!cutoffDate || !(cutoffDate instanceof Date)) {
      throw new ValidationError('Valid cutoff date is required', 'cutoffDate', cutoffDate);
    }

    const initialCount = this.bookings.length;
    this.bookings = this.bookings.filter(booking => {
      const bookingDate = new Date(booking.date);
      return bookingDate >= cutoffDate;
    });

    const removedCount = initialCount - this.bookings.length;

    if (removedCount > 0) {
      // Add audit log entry
      this._addAuditEntry(AuditLogEntry.createEntry(
        'system',
        'cleanup',
        memberId,
        { action: 'clearOldBookings', removedCount, cutoffDate: cutoffDate.toISOString() }
      ));
    }

    return removedCount;
  }

  /**
   * Get audit log entries with filtering options
   * @param {Object} options - Filtering options
   * @returns {Array} Filtered audit log entries
   */
  getAuditLog(options = {}) {
    let filteredLog = [...this.auditLog];

    if (options.start && options.end) {
      filteredLog = filteredLog.filter(entry =>
        entry.withinDateRange(options.start, options.end)
      );
    }

    if (options.memberId) {
      filteredLog = filteredLog.filter(entry => entry.madeBy(options.memberId));
    }

    if (options.entityType) {
      filteredLog = filteredLog.filter(entry => entry.entityType === options.entityType);
    }

    if (options.action) {
      filteredLog = filteredLog.filter(entry => entry.action === options.action);
    }

    return filteredLog.sort((a, b) => b.timestamp - a.timestamp); // Most recent first
  }

  /**
   * Remove audit log entries older than cutoff date
   * @param {Date} cutoffDate - Date before which entries should be removed
   * @param {string} memberId - Member performing the cleanup
   * @returns {number} Number of entries removed
   */
  purgeAuditLog(cutoffDate, memberId) {
    validateMember(memberId, 'purge_audit_log');

    if (!cutoffDate || !(cutoffDate instanceof Date)) {
      throw new ValidationError('Valid cutoff date is required', 'cutoffDate', cutoffDate);
    }

    const initialCount = this.auditLog.length;
    this.auditLog = this.auditLog.filter(entry => entry.timestamp >= cutoffDate);
    const removedCount = initialCount - this.auditLog.length;

    if (removedCount > 0) {
      // Add audit log entry for the purge itself
      this._addAuditEntry(AuditLogEntry.createEntry(
        'system',
        'audit-purge',
        memberId,
        { action: 'purgeAuditLog', removedCount, cutoffDate: cutoffDate.toISOString() }
      ));
    }

    return removedCount;
  }

  /**
   * Perform full system cleanup based on retention settings
   * @param {string} memberId - Member performing the cleanup
   * @returns {Object} Cleanup summary
   */
  cleanupSystem(memberId) {
    validateMember(memberId, 'system_cleanup');

    const now = new Date();
    
    // Check if cleanup is needed
    const daysSinceLastCleanup = (now - this._lastCleanup) / (1000 * 60 * 60 * 24);
    if (!this.settings.dataRetention.autoCleanupEnabled || 
        daysSinceLastCleanup < this.settings.dataRetention.cleanupIntervalDays) {
      return {
        performed: false,
        reason: 'Cleanup not needed yet',
        daysSinceLastCleanup: Math.floor(daysSinceLastCleanup)
      };
    }

    const bookingCutoff = new Date();
    bookingCutoff.setDate(bookingCutoff.getDate() - this.settings.dataRetention.bookingRetentionDays);

    const auditCutoff = new Date();
    auditCutoff.setDate(auditCutoff.getDate() - this.settings.dataRetention.auditLogRetentionDays);

    const removedBookings = this.clearOldBookings(bookingCutoff, memberId);
    const removedAuditEntries = this.purgeAuditLog(auditCutoff, memberId);

    this._lastCleanup = now;

    return {
      performed: true,
      removedBookings,
      removedAuditEntries,
      bookingCutoff: bookingCutoff.toISOString(),
      auditCutoff: auditCutoff.toISOString()
    };
  }

  /**
   * Convert to JSON string for persistence
   * @returns {string} JSON string representation
   */
  toJSON() {
    const data = {
      _id: this._id,
      club: this.club,
      settings: this.settings,
      greens: this.greens.map(green => JSON.parse(green.toJSON())),
      bookings: this.bookings.map(booking => JSON.parse(booking.toJSON())),
      bookingTypes: this.bookingTypes.map(type => JSON.parse(type.toJSON())),
      members: this.members.map(member => ({ ...member })),
      timeSlots: this.timeSlots.map(slot => JSON.parse(slot.toJSON())),
      auditLog: this.auditLog.map(entry => JSON.parse(entry.toJSON())),
      _lastCleanup: this._lastCleanup.toISOString()
    };
    return JSON.stringify(data);
  }

  /**
   * Create BookingSystem instance from JSON string
   * @param {string} jsonString - JSON string data
   * @returns {BookingSystem} New BookingSystem instance
   */
  static fromJSON(jsonString) {
    if (!jsonString || typeof jsonString !== 'string') {
      throw new ValidationError('Valid JSON string is required');
    }

    let jsonData;
    try {
      jsonData = JSON.parse(jsonString);
    } catch (error) {
      throw new ValidationError('Invalid JSON string format');
    }

    return new BookingSystem(jsonData);
  }

    /**
   * Create enhanced booking object with resolved references for display purposes
   * @param {string} bookingId - The booking ID to enhance
   * @param {string} [currentMemberId] - Current member ID for permission calculations
   * @returns {Object|null} Enhanced booking object with resolved data, or null if booking not found
   * @throws {ValidationError} When bookingId is invalid
   */
  createDisplayBooking(bookingId, currentMemberId = null) {
    if (!bookingId || typeof bookingId !== 'string') {
      throw new ValidationError('Valid booking ID is required', 'bookingId', bookingId);
    }

    // Find the base booking
    const booking = this.bookings.find(b => b._id === bookingId);
    if (!booking) {
      return null;
    }

    // Resolve green and rink
    const green = this._findGreenByRinkId(booking.rinkId);
    const rink = green?.getRink(booking.rinkId) || null;

    // Resolve participants
    const participantObjects = booking.participants.map(memberId => {
      const member = this.members.find(m => m._id === memberId);
      return member || { 
        _id: memberId, 
        name: 'Unknown Member', 
        preferences: {} 
      };
    });

    // Resolve booking type
    const bookingType = this.bookingTypes.find(bt => bt._id === booking.bookingTypeId) || null;

    // Get conflicts
    const conflicts = this.findBookingConflicts(
      booking.rinkId, 
      booking.date, 
      booking.start, 
      booking.end, 
      booking._id
    );

    // Create enhanced booking object with computed properties
    return {
      // Spread all original booking properties
      ...booking,

      // Add resolved references
      green,
      rink,
      participantObjects,
      bookingType,

      // Add computed display properties
      get displayName() {
        return green && rink ? `${green.name} - Rink ${rink.number}` : 'Unknown Location';
      },

      get formattedDate() {
        return booking.date.toLocaleDateString('en-GB', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      },
  
      get formattedStartTime() {
        return booking.start.toLocaleTimeString('en-GB', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        });
      },
  
      get formattedEndTime() {
        return booking.end.toLocaleTimeString('en-GB', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        });
      },
      
      get durationText() {
        const duration = calculateDuration(booking.start, booking.end);
        const hours = Math.floor(duration / 60);
        const minutes = duration % 60;
        
        if (hours === 0) {
          return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
        } else if (minutes === 0) {
          return `${hours} hour${hours !== 1 ? 's' : ''}`;
        } else {
          return `${hours}h ${minutes}m`;
        }
      },

      get canEdit() {
        return currentMemberId === booking.memberId && booking.canModify();
      },

      get canCancel() {
        return currentMemberId === booking.memberId && booking.canCancel();
      },

      get conflicts() {
        if (conflicts.length === 0) {
          return { type: 'none', severity: 'none' };
        }

        return {
          type: 'time',
          conflictingBookings: conflicts,
          severity: 'error'
        };
      }
    };
  }

  // Private helper methods

  /**
   * Find green that contains the specified rink
   * @private
   */
  _findGreenByRinkId(rinkId) {
    return this.greens.find(green => green.getRink(rinkId) !== null);
  }

  /**
   * Validate booking rules
   * @private
   */
  _validateBookingRules(bookingData) {
    const rules = this.settings.bookingRules;
    const now = new Date();
    
    // Check advance booking limit
    const daysInAdvance = (bookingData.date - now) / (1000 * 60 * 60 * 24);
    if (daysInAdvance > rules.maxAdvanceBookingDays) {
      throw new ValidationError(
        `Cannot book more than ${rules.maxAdvanceBookingDays} days in advance`,
        'advanceBooking',
        daysInAdvance
      );
    }

    // Check minimum advance booking time
    const hoursInAdvance = (bookingData.start - now) / (1000 * 60 * 60);
    if (hoursInAdvance < rules.minAdvanceBookingHours) {
      throw new ValidationError(
        `Must book at least ${rules.minAdvanceBookingHours} hours in advance`,
        'minAdvanceBooking',
        hoursInAdvance
      );
    }

    // Check booking duration
    const duration = calculateDuration(bookingData.start, bookingData.end);
    if (duration < rules.minBookingDuration) {
      throw new ValidationError(
        `Booking duration must be at least ${rules.minBookingDuration} minutes`,
        'minDuration',
        duration
      );
    }

    if (duration > rules.maxBookingDuration) {
      throw new ValidationError(
        `Booking duration cannot exceed ${rules.maxBookingDuration} minutes`,
        'maxDuration',
        duration
      );
    }
  }

  /**
   * Find booking conflicts for a given rink, date, and time range.
   *
   * @param {string} rinkId - The ID of the rink to check for conflicts.
   * @param {Date} date - The date of the booking to check.
   * @param {Date} start - The start time of the booking.
   * @param {Date} end - The end time of the booking.
   * @param {string|null} [excludeBookingId=null] - Optional booking ID to exclude from conflict check (e.g., when modifying an existing booking).
   * @returns {Array} Array of conflicting Booking instances.
   */
  findBookingConflicts(rinkId, date, start, end, excludeBookingId = null) {
    return this.bookings.filter(booking => {
      if (booking._id === excludeBookingId) return false;
      if (booking.status === 'cancelled') return false;
      if (booking.rinkId !== rinkId) return false;
      if (booking.date.toDateString() !== date.toDateString()) return false;

      // Check time overlap
      return booking.start < end && booking.end > start;
    });
  }

  /**
   * Add audit log entry
   * @private
   */
  _addAuditEntry(auditEntry) {
    this.auditLog.push(auditEntry);
  }
} 